import React, { useEffect, type ErrorInfo } from 'react';
import { EXPERIMENT } from '../../common/experiments/get-experiment-group';

import TemplatePreview from '../../ui/template-preview';
import PreprovisionLanding from '../../ui/preprovision-landing';
import SpaceOrPageNotFound from '../../ui/project-pages-improvement/error-states/space-or-page-not-found/connected';
import { ErrorBoundary } from 'react-error-boundary';

import { type Props } from '../types';
import ActivationInProgressPage from '../../ui/activation-in-progress-page/connected';
import { useRouter } from '../../containers/view-router-context';
import { Routes } from '../../containers/view-router-context/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

export const ActivePage = ({
	EmbeddedProductUpdater,
	ConfluenceTree,
	ConfluenceActivating,
	ConnectSpace,
	ServerError,
	simplifiedLandingCohort,
	onFireErrorEvent,
	renderFullHeader,
}: Props & { activeRoute?: Routes }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activeRoute } = useRouter();

	const isSimplifiedLandingEnabled = simplifiedLandingCohort === EXPERIMENT;

	const onSimplifiedLandingErrorEvent = (error: Error, info: ErrorInfo) => {
		if (onFireErrorEvent) {
			onFireErrorEvent(error, info, {
				id: 'simplifiedLandingExperiment',
				packageName: 'projectPagesExamplePageTree',
				teamName: 'starburst',
			});
		}
	};

	useEffect(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'activePage viewed', {
			route: activeRoute,
		});
	}, [activeRoute, createAnalyticsEvent]);

	switch (activeRoute) {
		case Routes.TemplatePreview:
			if (isSimplifiedLandingEnabled) {
				return (
					<ErrorBoundary onError={onSimplifiedLandingErrorEvent} fallback={<TemplatePreview />}>
						<PreprovisionLanding />
					</ErrorBoundary>
				);
			}
			return <TemplatePreview />;
		case Routes.ConfluenceActivating:
			return <ConfluenceActivating />;
		case Routes.ConfluenceActivatingOnDemand:
			return <ActivationInProgressPage />;
		case Routes.ConnectSpace:
			return <ConnectSpace key="ConnectSpace" />;
		case Routes.ServerError:
			return <ServerError />;
		case Routes.ServerErrorAccess:
			return <ServerError type="access" />;
		case Routes.SpaceOrPageNotFound:
			return <SpaceOrPageNotFound />;
		default:
			return (
				<ConfluenceTree
					EmbeddedProductUpdater={EmbeddedProductUpdater}
					showDraftToggle={renderFullHeader ?? true}
				/>
			);
	}
};
